import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Api, type PORequestDto, HttpClient } from '../../generated';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import { httpClient } from '../../utils/apiCall';
import type { ExchangeRateInfo, ManualRates } from '../../components/Shared/ExchangeRatePrompt';

// Use generated API types
export type PORequestItem = PORequestDto;

/** Response shape for GET /api/PORequest/{poRequestId}/quote-items (includes round) */
export interface PORequestQuoteItemsResponse {
  data?: unknown;
  /** Teklif turu (round) */
  round?: number | null;
}

/** GET /api/PORequest/{poRequestId}/quote-items data alanı */
export interface PORequestQuoteItemsResult {
  items: any[];
  exchangeRates: ExchangeRateInfo;
}

// Enhanced type for create request with better validation
export type CreatePORequestRequest = Omit<PORequestDto, 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'approvedQuantity' | 'categoryName'>;

// Enhanced type for update request
export type UpdatePORequestRequest = { 
  id: number; 
  requestData: Partial<CreatePORequestRequest> 
};

// Query parameters for filtering
export interface PORequestQueryParams {
  RequestStatus?: string;
  CreatedBy?: number;
  ApproverUserId?: number;
  DecisionUserId?: number;
  PurchaserId?: number;
  pageIndex?: number;
  pageSize?: number;
  searchTerm?: string;
  categoryId?: number;
  isUrgent?: boolean;
}

// Create API client instance compatible with generated API
const apiClient = new HttpClient({
  baseURL: API_BASE_URL,
  securityWorker: async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return {};
  },
  secure: true,
});

const api = new Api(apiClient);

interface PORequestState {
  loading: boolean;
  items: PORequestItem[];
  pagination: {
    index: number;
    size: number;
    count: number;
    pages: number;
    hasPrevious: boolean;
    hasNext: boolean;
  } | null;
  error: string | null;
  lastFetchParams: PORequestQueryParams | null;
  selectedItems: number[];
  filters: {
    status: string[];
    category: string[];
    urgency: boolean | null;
  };
  quoteItems: any[];
  evaluationReadyItems: PORequestItem[];
  awardedSuppliers: any[];
  negotiationSuppliers: any[];
  supplierAwardedQuoteItems: any[];
  supplierNegotiationQuoteItems: any[];
  poRequestUserList: any[];
  statusCounts: any;
  statusMatrix: any;
  operationLoading: {
    quoteItems: boolean;
    evaluationReady: boolean;
    awardedSuppliers: boolean;
    negotiationSuppliers: boolean;
    supplierAwardedQuoteItems: boolean;
    supplierNegotiationQuoteItems: boolean;
    poRequestUserList: boolean;
    statusCounts: boolean;
    statusMatrix: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    statusUpdate: boolean;
    setPendingByUser: boolean;
    approve: boolean;
    getDetail: boolean;
    deleteWithQuoteItems: boolean;
  };
  // Enhanced error handling
  errors: {
    fetch: string | null;
    create: string | null;
    update: string | null;
    delete: string | null;
    deleteWithQuoteItems: string | null;
    statusUpdate: string | null;
    quoteItems: string | null;
    evaluationReady: string | null;
    awardedSuppliers: string | null;
    negotiationSuppliers: string | null;
    supplierAwardedQuoteItems: string | null;
    supplierNegotiationQuoteItems: string | null;
    poRequestUserList: string | null;
    statusCounts: string | null;
    statusMatrix: string | null;
    setPendingByUser: string | null;
    approve: string | null;
  };
}

const initialState: PORequestState = {
  loading: false,
  items: [],
  pagination: null,
  error: null,
  lastFetchParams: null,
  selectedItems: [],
  filters: {
    status: [],
    category: [],
    urgency: null,
  },
  quoteItems: [],
  evaluationReadyItems: [],
  awardedSuppliers: [],
  negotiationSuppliers: [],
  supplierAwardedQuoteItems: [],
  supplierNegotiationQuoteItems: [],
  poRequestUserList: [],
  statusCounts: null,
  statusMatrix: null,
  operationLoading: {
    quoteItems: false,
    evaluationReady: false,
    awardedSuppliers: false,
    negotiationSuppliers: false,
    supplierAwardedQuoteItems: false,
    supplierNegotiationQuoteItems: false,
    poRequestUserList: false,
    statusCounts: false,
    statusMatrix: false,
    create: false,
    update: false,
    delete: false,
    statusUpdate: false,
    setPendingByUser: false,
    approve: false,
    getDetail: false,
    deleteWithQuoteItems: false,
  },
  errors: {
    fetch: null,
    create: null,
    update: null,
    delete: null,
    deleteWithQuoteItems: null,
    statusUpdate: null,
    quoteItems: null,
    evaluationReady: null,
    awardedSuppliers: null,
    negotiationSuppliers: null,
    supplierAwardedQuoteItems: null,
    supplierNegotiationQuoteItems: null,
    poRequestUserList: null,
    statusCounts: null,
    statusMatrix: null,
    setPendingByUser: null,
    approve: null,
  },
};

export const getPORequests = createAsyncThunk(
  "poRequest/getPORequests",
  async (params: PORequestQueryParams = {}, thunkAPI) => {
    try {
      // Enhanced query parameters with better error handling
      const queryParams: any = {};
      
      if (params.RequestStatus) {
        queryParams.RequestStatus = params.RequestStatus;
      }
      
      if (params.CreatedBy) {
        queryParams.CreatedBy = params.CreatedBy;
      }
      
      if (params.ApproverUserId) {
        queryParams.ApproverUserId = params.ApproverUserId;
      }
      
      if (params.DecisionUserId) {
        queryParams.DecisionUserId = params.DecisionUserId;
      }
      
      if (params.PurchaserId) {
        queryParams.PurchaserId = params.PurchaserId;
      }
      
      if (params.pageIndex !== undefined) {
        queryParams.PageIndex = params.pageIndex;
      }
      
      if (params.pageSize !== undefined) {
        queryParams.PageSize = params.pageSize;
      }
      
      if (params.searchTerm) {
        queryParams.SearchTerm = params.searchTerm;
      }
      
      if (params.categoryId) {
        queryParams.CategoryId = params.categoryId;
      }
      
      if (params.isUrgent !== undefined) {
        queryParams.IsUrgent = params.isUrgent;
      }
      
      // Cache busting headers - her seferinde fresh data
      const response = await api.api.poRequestList(queryParams, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return { response, params };
    } catch (error) {
      
      const errorMessage = error instanceof Error ? error.message : 'Talepler yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);


export const createPORequest = createAsyncThunk(
  "poRequest/createPORequest",
  async (requestData: PORequestDto, thunkAPI) => {
    try {
      // Validate required fields
      if (!requestData.itemName || !requestData.requestedQuantity) {
        throw new Error('Ürün adı ve miktar zorunludur');
      }
      
      if (requestData.requestedQuantity <= 0) {
        throw new Error('Miktar 0\'dan büyük olmalıdır');
      }
      
      // Cache busting headers - her seferinde fresh data
      const response = await api.api.poRequestCreate(requestData, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response;
    } catch (error) {
      
      const errorMessage = error instanceof Error ? error.message : 'Talep oluşturulurken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const updatePORequest = createAsyncThunk(
  "poRequest/updatePORequest",
  async ({ id, requestData }: UpdatePORequestRequest, thunkAPI) => {
    try {
      // Validate ID
      if (!id || id <= 0) {
        throw new Error('Geçerli bir ID gerekli');
      }
      
      // Validate quantity if provided
      if (requestData.requestedQuantity !== undefined && requestData.requestedQuantity <= 0) {
        throw new Error('Miktar 0\'dan büyük olmalıdır');
      }
      
      // API'de update endpoint'i olmadığı için create kullanıyoruz
      // ID'yi requestData'ya ekliyoruz
      // Cache busting headers - her seferinde fresh data
      const response = await api.api.poRequestCreate({ ...requestData, id } as PORequestDto, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return { id, response };
    } catch (error) {
      
      const errorMessage = error instanceof Error ? error.message : 'Talep güncellenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const getPORequestDetail = createAsyncThunk(
  "poRequest/getPORequestDetail",
  async (id: number, thunkAPI) => {
    try {
      const response = await api.api.poRequestDetail({ id });
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deletePORequest = createAsyncThunk(
  "poRequest/deletePORequest",
  async (id: number, thunkAPI) => {
    try {
      // Cache busting headers - her seferinde fresh data
      await api.api.poRequestDelete({ id }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return id;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const deletePORequestWithQuoteItems = createAsyncThunk(
  "poRequest/deletePORequestWithQuoteItems",
  async (id: number, thunkAPI) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/PORequest/delete-with-quote-items/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Talep ve teklif öğeleri silinirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const updatePORequestStatus = createAsyncThunk(
  "poRequest/updatePORequestStatus",
  async ({ ids, status, currentItems, approvedQuantity }: { ids: number[], status: string, currentItems: PORequestItem[], approvedQuantity?: number }, thunkAPI) => {
    try {
      // Her bir talep için ayrı ayrı update yapıyoruz
      const promises = ids.map(async (id) => {
        // Mevcut talep verilerini bul
        const currentItem = currentItems.find(item => item.id === id);
        if (!currentItem) {
          throw new Error(`Talep ID ${id} bulunamadı`);
        }
        
        // Mevcut verileri al, requestStatus ve approvedQuantity'yi güncelle
        const finalApprovedQuantity = approvedQuantity !== undefined ? approvedQuantity : (currentItem.approvedQuantity ?? null);
        const requestData: PORequestDto = {
          ...currentItem,
          requestStatus: status,
          approvedQuantity: finalApprovedQuantity
        };
        
        
        
        
        // Cache busting headers - her seferinde fresh data
        const response = await api.api.poRequestCreate(requestData, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        
        return response;
      });
      
      const responses = await Promise.all(promises);
      return { ids, status, responses };
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

// New specific status update endpoints
export const setPORequestApproved = createAsyncThunk(
  "poRequest/setPORequestApproved",
  async (id: number, thunkAPI) => {
    try {
      // Cache busting headers - her seferinde fresh data
      const response = await api.api.poRequestSetApprovedPartialUpdate({ id }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return { id, response };
    } catch (error) {
      let errorMessage = 'Onaylama işlemi başarısız';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const setPORequestApprovedQuantity = createAsyncThunk(
  "poRequest/setPORequestApprovedQuantity",
  async ({ id, quantity }: { id: number, quantity: number }, thunkAPI) => {
    try {
      
      // Cache busting headers - her seferinde fresh data
      const response = await api.api.poRequestSetApprovedQuantityPartialUpdate({ id }, quantity, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      return { id, quantity, response };
    } catch (error) {
      let errorMessage = 'Onaylanan miktar güncelleme başarısız';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const setPORequestRejected = createAsyncThunk(
  "poRequest/setPORequestRejected",
  async (id: number, thunkAPI) => {
    try {
      // Cache busting headers - her seferinde fresh data
      const response = await api.api.poRequestSetRejectedPartialUpdate({ id }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return { id, response };
    } catch (error) {
      let errorMessage = 'Reddetme işlemi başarısız';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Set PORequest Pending by User
export const setPORequestPendingByUser = createAsyncThunk(
  "poRequest/setPORequestPendingByUser",
  async (createdBy: number, thunkAPI) => {
    try {
      // Swagger'da gerçek endpoint var: /api/PORequest/set-pending-by-user/{createdBy}
      // Bu endpoint kullanıcının tüm taleplerini tek seferde beklemede durumuna alır
      
      // Doğrudan HTTP isteği yap - generated API'de henüz bu endpoint yok
      const response = await fetch(`${API_BASE_URL}/api/PORequest/set-pending-by-user/${createdBy}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return { createdBy, response: data };
    } catch (error) {
      let errorMessage = 'Kullanıcı taleplerini beklemede durumuna alma başarısız';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Approve PORequest (Bulk approval)
export const approvePORequest = createAsyncThunk(
  "poRequest/approvePORequest",
  async (approveData: { id: number; approvedQuantity: number }, thunkAPI) => {
    try {
      // Validate required fields
      if (!approveData.id || approveData.id <= 0) {
        throw new Error('Geçerli bir ID gerekli');
      }
      
      if (approveData.approvedQuantity < 0) {
        throw new Error('Onaylanan miktar 0\'dan küçük olamaz');
      }
      
      // Bu endpoint henüz generated API'de yok, mevcut endpoint'leri kullanarak simüle ediyoruz
      // Önce approved quantity'yi set et, sonra approved status'a geç
      const setQuantityResponse = await api.api.poRequestSetApprovedQuantityPartialUpdate(
        { id: approveData.id }, 
        approveData.approvedQuantity,
        {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        }
      );
      
      const setApprovedResponse = await api.api.poRequestSetApprovedPartialUpdate({ id: approveData.id }, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      return { 
        id: approveData.id, 
        approvedQuantity: approveData.approvedQuantity, 
        response: { setQuantityResponse, setApprovedResponse }
      };
    } catch (error) {
      let errorMessage = 'Toplu onaylama işlemi başarısız';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String(error.message);
      }
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Quote Items
// usdRate/eurRate: Netsis'te bugünün kuru yoksa kullanıcının o an için girdiği kur (kaydedilmez)
export const getPORequestQuoteItems = createAsyncThunk(
  "poRequest/getPORequestQuoteItems",
  async ({ poRequestId, usdRate, eurRate }: { poRequestId: number } & ManualRates, thunkAPI) => {
    try {
      const response = await httpClient.get<PORequestQuoteItemsResult | any[]>(
        `${API_ENDPOINTS.PO_REQUEST_QUOTE.QUOTE_ITEMS}/${poRequestId}/quote-items`,
        { params: { usdRate, eurRate } }
      );
      // Eski backend düz liste döner; yeni backend { items, exchangeRates }. İkisini de aynı şekle getir.
      const data: PORequestQuoteItemsResult = Array.isArray(response.data)
        ? { items: response.data, exchangeRates: { missingCurrencies: [], manualRates: {} } }
        : response.data;
      return { poRequestId, response: { ...response, data } };
    } catch (error) {
      
      const errorMessage = error instanceof Error ? error.message : 'Teklif öğeleri yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Evaluation Ready List - Using existing poRequestList with status filter
export const getPORequestEvaluationReadyList = createAsyncThunk(
  "poRequest/getPORequestEvaluationReadyList",
  async (_, thunkAPI) => {
    try {
      // Use existing poRequestList with status filter for evaluation ready items
      const response = await api.api.poRequestList({ RequestStatus: 'EvaluationReady' });
      return response;
    } catch (error) {
      
      const errorMessage = error instanceof Error ? error.message : 'Değerlendirme hazır talepler yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Awarded Suppliers List
export const getPORequestAwardedSuppliersList = createAsyncThunk(
  "poRequest/getPORequestAwardedSuppliersList",
  async (cacheBust: boolean = true, thunkAPI) => {
    try {
      // Cache-busting için headers kullan
      const headers = cacheBust ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      } : {};
      
      const response = await api.api.poRequestAwardedSuppliersList({
        headers
      });
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Onaylanmış tedarikçiler yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Negotiation Suppliers List
export const getPORequestNegotiationSuppliersList = createAsyncThunk(
  "poRequest/getPORequestNegotiationSuppliersList",
  async (cacheBust: boolean = false, thunkAPI) => {
    try {
      // Cache-busting için headers kullan
      const headers = cacheBust ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      } : {};
      
      const response = await api.api.poRequestNegotiationSuppliersList({
        headers
      });
      return response;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Müzakereli tedarikçiler yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Supplier Awarded Quote Items List
export const getPORequestSupplierAwardedQuoteItemsList = createAsyncThunk(
  "poRequest/getPORequestSupplierAwardedQuoteItemsList",
  async ({ supplierId, cacheBust }: { supplierId: number; cacheBust?: boolean }, thunkAPI) => {
    try {
      // Cache-busting için headers kullan
      const headers = cacheBust ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      } : {};
      
      const response = await api.api.poRequestSupplierAwardedQuoteItemsList({ 
        supplierId
      }, {
        headers
      });
      return { supplierId, response };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Tedarikçi onaylanmış teklif öğeleri yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Supplier Negotiation Quote Items List
export const getPORequestSupplierNegotiationQuoteItemsList = createAsyncThunk(
  "poRequest/getPORequestSupplierNegotiationQuoteItemsList",
  async ({ supplierId, cacheBust }: { supplierId: number; cacheBust?: boolean }, thunkAPI) => {
    try {
      // Cache-busting için headers kullan
      const headers = cacheBust ? {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      } : {};
      
      const response = await api.api.poRequestSupplierNegotiationQuoteItemsList({ 
        supplierId
      }, {
        headers
      });
      return { supplierId, response };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Tedarikçi müzakereli teklif öğeleri yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest User List
export const getPORequestUserList = createAsyncThunk(
  "poRequest/getPORequestUserList",
  async (cacheBust: boolean = false, thunkAPI) => {
    try {
      // Swagger'da gerçek endpoint var: /api/PORequest/po-request-user-list
      // Bu endpoint kullanıcı listesini döndürür
      
      // Cache-busting için headers kullan
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      };
      
      if (cacheBust) {
        headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        headers['Pragma'] = 'no-cache';
        headers['Expires'] = '0';
      }
      
      const response = await fetch(`${API_BASE_URL}/api/PORequest/po-request-user-list`, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Kullanıcı listesi yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// Get PORequest Status Counts
export const getStatusCounts = createAsyncThunk(
  "poRequest/getStatusCounts",
  async (params: { 
    RequestStatus?: string; 
    CreatedBy?: number; 
    ApproverUserId?: number; 
    DecisionUserId?: number; 
    PurchaserId?: number;
  } = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.RequestStatus) queryParams.append('RequestStatus', params.RequestStatus);
      if (params.CreatedBy) queryParams.append('CreatedBy', params.CreatedBy.toString());
      if (params.ApproverUserId) queryParams.append('ApproverUserId', params.ApproverUserId.toString());
      if (params.DecisionUserId) queryParams.append('DecisionUserId', params.DecisionUserId.toString());
      if (params.PurchaserId) queryParams.append('PurchaserId', params.PurchaserId.toString());

      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/PORequest/status-counts${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Durum sayıları yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

// GET /api/PORequest/status-matrix?startDate=...&endDate=...
export const getStatusMatrix = createAsyncThunk(
  "poRequest/getStatusMatrix",
  async (params: { startDate?: string; endDate?: string } = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}/api/PORequest/status-matrix${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Status matrix yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

const poRequestSlice = createSlice({
  name: "poRequest",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.errors = {
        fetch: null,
        create: null,
        update: null,
        delete: null,
        deleteWithQuoteItems: null,
        statusUpdate: null,
        quoteItems: null,
        evaluationReady: null,
        awardedSuppliers: null,
        negotiationSuppliers: null,
        supplierAwardedQuoteItems: null,
        supplierNegotiationQuoteItems: null,
        poRequestUserList: null,
        statusCounts: null,
        statusMatrix: null,
        setPendingByUser: null,
        approve: null,
      };
    },
    clearSpecificError: (state, action: PayloadAction<keyof PORequestState['errors']>) => {
      state.errors[action.payload] = null;
    },
    clearPORequests: (state) => {
      state.items = [];
      state.pagination = null;
      state.error = null;
      state.lastFetchParams = null;
      state.errors = {
        fetch: null,
        create: null,
        update: null,
        delete: null,
        deleteWithQuoteItems: null,
        statusUpdate: null,
        quoteItems: null,
        evaluationReady: null,
        awardedSuppliers: null,
        negotiationSuppliers: null,
        supplierAwardedQuoteItems: null,
        supplierNegotiationQuoteItems: null,
        poRequestUserList: null,
        statusCounts: null,
        statusMatrix: null,
        setPendingByUser: null,
        approve: null,
      };
    },
    setSelectedItems: (state, action: PayloadAction<number[]>) => {
      state.selectedItems = action.payload;
    },
    toggleSelectedItem: (state, action: PayloadAction<number>) => {
      const itemId = action.payload;
      const index = state.selectedItems.indexOf(itemId);
      if (index === -1) {
        state.selectedItems.push(itemId);
      } else {
        state.selectedItems.splice(index, 1);
      }
    },
    clearSelectedItems: (state) => {
      state.selectedItems = [];
    },
    setFilters: (state, action: PayloadAction<Partial<PORequestState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: [],
        category: [],
        urgency: null,
      };
    },
    clearQuoteItems: (state) => {
      state.quoteItems = [];
    },
    clearEvaluationReadyItems: (state) => {
      state.evaluationReadyItems = [];
    },
    clearAwardedSuppliers: (state) => {
      state.awardedSuppliers = [];
    },
    clearNegotiationSuppliers: (state) => {
      state.negotiationSuppliers = [];
    },
    clearSupplierAwardedQuoteItems: (state) => {
      state.supplierAwardedQuoteItems = [];
    },
    clearSupplierNegotiationQuoteItems: (state) => {
      state.supplierNegotiationQuoteItems = [];
    },
    clearPORequestUserList: (state) => {
      state.poRequestUserList = [];
    },
    clearStatusCounts: (state) => {
      state.statusCounts = null;
    },
    clearStatusMatrix: (state) => {
      state.statusMatrix = null;
    },
  },
  extraReducers(builder) {
    // Get PORequests
    builder.addCase(getPORequests.pending, (state) => {
      state.loading = true;
      state.error = null;
      state.errors.fetch = null;
    });
    builder.addCase(getPORequests.fulfilled, (state, action) => {
      state.loading = false;
      state.lastFetchParams = action.payload.params;
      
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const responseData = action.payload.response;
      if (Array.isArray(responseData)) {
        state.items = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        // If response has data property
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.items = data;
        } else if (data && data.items) {
          state.items = data.items;
          state.pagination = {
            index: data.index || 0,
            size: data.size || 0,
            count: data.count || 0,
            pages: data.pages || 0,
            hasPrevious: data.hasPrevious || false,
            hasNext: data.hasNext || false,
          };
        }
      }
      state.error = null;
      state.errors.fetch = null;
    });
    builder.addCase(getPORequests.rejected, (state, action) => {
      state.loading = false;
      const errorMessage = typeof action.payload === 'string' 
        ? action.payload 
        : 'Talepler yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.fetch = errorMessage;
    });

    // Create PORequest
    builder.addCase(createPORequest.pending, (state) => {
      state.operationLoading.create = true;
      state.error = null;
      state.errors.create = null;
    });
    builder.addCase(createPORequest.fulfilled, (state, action) => {
      state.operationLoading.create = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const newItem = (responseData as any).data;
        if (newItem && newItem.id) {
          // Check if item already exists to avoid duplicates
          const existingIndex = state.items.findIndex(item => item.id === newItem.id);
          if (existingIndex === -1) {
            state.items.unshift(newItem); // Add to beginning
          } else {
            state.items[existingIndex] = newItem; // Update existing
          }
        }
      }
      state.error = null;
      state.errors.create = null;
    });
    builder.addCase(createPORequest.rejected, (state, action) => {
      state.operationLoading.create = false;
      const errorMessage = typeof action.payload === 'string' 
        ? action.payload 
        : 'Talep oluşturulurken hata oluştu';
      state.error = errorMessage;
      state.errors.create = errorMessage;
    });

    // Update PORequest
    builder.addCase(updatePORequest.pending, (state) => {
      state.operationLoading.update = true;
      state.error = null;
      state.errors.update = null;
    });
    builder.addCase(updatePORequest.fulfilled, (state, action) => {
      state.operationLoading.update = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { id } = action.payload;
      const responseData = action.payload.response;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const updatedItem = (responseData as any).data;
        if (updatedItem && updatedItem.id) {
          const index = state.items.findIndex(item => item.id === id);
          if (index !== -1) {
            state.items[index] = updatedItem;
          }
        }
      }
      state.error = null;
      state.errors.update = null;
    });
    builder.addCase(updatePORequest.rejected, (state, action) => {
      state.operationLoading.update = false;
      const errorMessage = typeof action.payload === 'string' 
        ? action.payload 
        : 'Talep güncellenirken hata oluştu';
      state.error = errorMessage;
      state.errors.update = errorMessage;
    });

    // Get PORequest Detail
    builder.addCase(getPORequestDetail.pending, (state) => {
      state.operationLoading.getDetail = true;
      state.error = null;
    });
    builder.addCase(getPORequestDetail.fulfilled, (state, action) => {
      state.operationLoading.getDetail = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const responseData = action.payload;
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const updatedItem = (responseData as any).data;
        if (updatedItem && updatedItem.id) {
          const index = state.items.findIndex(item => item.id === updatedItem.id);
          if (index !== -1) {
            state.items[index] = updatedItem;
          } else {
            state.items.push(updatedItem);
          }
        }
      }
      state.error = null;
    });
    builder.addCase(getPORequestDetail.rejected, (state, action) => {
      state.operationLoading.getDetail = false;
      state.error = action.payload instanceof Error 
        ? action.payload.message 
        : 'Bir hata oluştu';
    });

    // Delete PORequest
    builder.addCase(deletePORequest.pending, (state) => {
      state.operationLoading.delete = true;
      state.error = null;
      state.errors.delete = null;
    });
    builder.addCase(deletePORequest.fulfilled, (state, action) => {
      state.operationLoading.delete = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      state.items = state.items.filter(item => item.id !== action.payload);
      state.error = null;
      state.errors.delete = null;
    });
    builder.addCase(deletePORequest.rejected, (state, action) => {
      state.operationLoading.delete = false;
      // Safely convert error to string
      let errorMessage = 'Bir hata oluştu';
      if (action.payload instanceof Error) {
        errorMessage = action.payload.message;
      } else if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload && typeof action.payload === 'object' && 'message' in action.payload) {
        errorMessage = String(action.payload.message);
      }
      state.error = errorMessage;
      state.errors.delete = errorMessage;
    });

    // Delete PORequest With Quote Items
    builder.addCase(deletePORequestWithQuoteItems.pending, (state) => {
      state.operationLoading.deleteWithQuoteItems = true;
      state.error = null;
      state.errors.deleteWithQuoteItems = null;
    });
    builder.addCase(deletePORequestWithQuoteItems.fulfilled, (state, action) => {
      state.operationLoading.deleteWithQuoteItems = false;
      state.items = state.items.filter(item => item.id !== action.payload);
      state.error = null;
      state.errors.deleteWithQuoteItems = null;
    });
    builder.addCase(deletePORequestWithQuoteItems.rejected, (state, action) => {
      state.operationLoading.deleteWithQuoteItems = false;
      const errorMessage = typeof action.payload === 'string' 
        ? action.payload 
        : 'Talep ve teklif öğeleri silinirken hata oluştu';
      state.error = errorMessage;
      state.errors.deleteWithQuoteItems = errorMessage;
    });

    // Update PORequest Status
    builder.addCase(updatePORequestStatus.pending, (state) => {
      state.operationLoading.statusUpdate = true;
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(updatePORequestStatus.fulfilled, (state, action) => {
      state.operationLoading.statusUpdate = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { ids, status } = action.payload;
      state.items = state.items.map(item => 
        ids.includes(item.id!) 
          ? { ...item, requestStatus: status }
          : item
      );
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(updatePORequestStatus.rejected, (state, action) => {
      state.operationLoading.statusUpdate = false;
      // Safely convert error to string
      let errorMessage = 'Bir hata oluştu';
      if (action.payload instanceof Error) {
        errorMessage = action.payload.message;
      } else if (typeof action.payload === 'string') {
        errorMessage = action.payload;
      } else if (action.payload && typeof action.payload === 'object' && 'message' in action.payload) {
        errorMessage = String(action.payload.message);
      }
      state.error = errorMessage;
      state.errors.statusUpdate = errorMessage;
    });

    // Set PORequest Approved
    builder.addCase(setPORequestApproved.pending, (state) => {
      state.operationLoading.statusUpdate = true;
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(setPORequestApproved.fulfilled, (state, action) => {
      state.operationLoading.statusUpdate = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { id } = action.payload;
      state.items = state.items.map(item => 
        item.id === id 
          ? { ...item, requestStatus: 'Approved' }
          : item
      );
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(setPORequestApproved.rejected, (state, action) => {
      state.operationLoading.statusUpdate = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Onaylama işlemi başarısız';
      state.error = errorMessage;
      state.errors.statusUpdate = errorMessage;
    });

    // Set PORequest Approved Quantity
    builder.addCase(setPORequestApprovedQuantity.pending, (state) => {
      state.operationLoading.statusUpdate = true;
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(setPORequestApprovedQuantity.fulfilled, (state, action) => {
      state.operationLoading.statusUpdate = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { id, quantity } = action.payload;
      state.items = state.items.map(item => 
        item.id === id 
          ? { ...item, approvedQuantity: quantity }
          : item
      );
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(setPORequestApprovedQuantity.rejected, (state, action) => {
      state.operationLoading.statusUpdate = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Onaylanan miktar güncelleme başarısız';
      state.error = errorMessage;
      state.errors.statusUpdate = errorMessage;
    });

    // Set PORequest Rejected
    builder.addCase(setPORequestRejected.pending, (state) => {
      state.operationLoading.statusUpdate = true;
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(setPORequestRejected.fulfilled, (state, action) => {
      state.operationLoading.statusUpdate = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { id } = action.payload;
      state.items = state.items.map(item => 
        item.id === id 
          ? { ...item, requestStatus: 'Rejected' }
          : item
      );
      state.error = null;
      state.errors.statusUpdate = null;
    });
    builder.addCase(setPORequestRejected.rejected, (state, action) => {
      state.operationLoading.statusUpdate = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Reddetme işlemi başarısız';
      state.error = errorMessage;
      state.errors.statusUpdate = errorMessage;
    });

    // Get PORequest Quote Items
    builder.addCase(getPORequestQuoteItems.pending, (state) => {
      state.operationLoading.quoteItems = true;
      state.error = null;
      state.errors.quoteItems = null;
    });
    builder.addCase(getPORequestQuoteItems.fulfilled, (state) => {
      state.operationLoading.quoteItems = false;
      // Cache'leme yapmıyoruz - her seferinde fresh data
      state.error = null;
      state.errors.quoteItems = null;
    });
    builder.addCase(getPORequestQuoteItems.rejected, (state, action) => {
      state.operationLoading.quoteItems = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Teklif öğeleri yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.quoteItems = errorMessage;
    });

    // Get PORequest Evaluation Ready List
    builder.addCase(getPORequestEvaluationReadyList.pending, (state) => {
      state.operationLoading.evaluationReady = true;
      state.error = null;
      state.errors.evaluationReady = null;
    });
    builder.addCase(getPORequestEvaluationReadyList.fulfilled, (state, action) => {
      state.operationLoading.evaluationReady = false;
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.evaluationReadyItems = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.evaluationReadyItems = data;
        } else if (data && data.items) {
          state.evaluationReadyItems = data.items;
        }
      }
      state.error = null;
      state.errors.evaluationReady = null;
    });
    builder.addCase(getPORequestEvaluationReadyList.rejected, (state, action) => {
      state.operationLoading.evaluationReady = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Değerlendirme hazır talepler yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.evaluationReady = errorMessage;
    });

    // Get PORequest Awarded Suppliers List
    builder.addCase(getPORequestAwardedSuppliersList.pending, (state) => {
      state.operationLoading.awardedSuppliers = true;
      state.error = null;
      state.errors.awardedSuppliers = null;
    });
    builder.addCase(getPORequestAwardedSuppliersList.fulfilled, (state, action) => {
      state.operationLoading.awardedSuppliers = false;
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.awardedSuppliers = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.awardedSuppliers = data;
        }
      }
      state.error = null;
      state.errors.awardedSuppliers = null;
    });
    builder.addCase(getPORequestAwardedSuppliersList.rejected, (state, action) => {
      state.operationLoading.awardedSuppliers = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Onaylanmış tedarikçiler yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.awardedSuppliers = errorMessage;
    });

    // Get PORequest Negotiation Suppliers List
    builder.addCase(getPORequestNegotiationSuppliersList.pending, (state) => {
      state.operationLoading.negotiationSuppliers = true;
      state.error = null;
      state.errors.negotiationSuppliers = null;
    });
    builder.addCase(getPORequestNegotiationSuppliersList.fulfilled, (state, action) => {
      state.operationLoading.negotiationSuppliers = false;
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.negotiationSuppliers = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.negotiationSuppliers = data;
        }
      }
      state.error = null;
      state.errors.negotiationSuppliers = null;
    });
    builder.addCase(getPORequestNegotiationSuppliersList.rejected, (state, action) => {
      state.operationLoading.negotiationSuppliers = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Müzakereli tedarikçiler yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.negotiationSuppliers = errorMessage;
    });

    // Get PORequest Supplier Awarded Quote Items List
    builder.addCase(getPORequestSupplierAwardedQuoteItemsList.pending, (state) => {
      state.operationLoading.supplierAwardedQuoteItems = true;
      state.error = null;
      state.errors.supplierAwardedQuoteItems = null;
    });
    builder.addCase(getPORequestSupplierAwardedQuoteItemsList.fulfilled, (state, action) => {
      state.operationLoading.supplierAwardedQuoteItems = false;
      const { response } = action.payload;
      if (Array.isArray(response)) {
        state.supplierAwardedQuoteItems = response;
      } else if (response && typeof response === 'object' && 'data' in response) {
        const data = (response as any).data;
        if (Array.isArray(data)) {
          state.supplierAwardedQuoteItems = data;
        }
      }
      state.error = null;
      state.errors.supplierAwardedQuoteItems = null;
    });
    builder.addCase(getPORequestSupplierAwardedQuoteItemsList.rejected, (state, action) => {
      state.operationLoading.supplierAwardedQuoteItems = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Tedarikçi onaylanmış teklif öğeleri yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.supplierAwardedQuoteItems = errorMessage;
    });

    // Get PORequest Supplier Negotiation Quote Items List
    builder.addCase(getPORequestSupplierNegotiationQuoteItemsList.pending, (state) => {
      state.operationLoading.supplierNegotiationQuoteItems = true;
      state.error = null;
      state.errors.supplierNegotiationQuoteItems = null;
    });
    builder.addCase(getPORequestSupplierNegotiationQuoteItemsList.fulfilled, (state, action) => {
      state.operationLoading.supplierNegotiationQuoteItems = false;
      const { response } = action.payload;
      if (Array.isArray(response)) {
        state.supplierNegotiationQuoteItems = response;
      } else if (response && typeof response === 'object' && 'data' in response) {
        const data = (response as any).data;
        if (Array.isArray(data)) {
          state.supplierNegotiationQuoteItems = data;
        }
      }
      state.error = null;
      state.errors.supplierNegotiationQuoteItems = null;
    });
    builder.addCase(getPORequestSupplierNegotiationQuoteItemsList.rejected, (state, action) => {
      state.operationLoading.supplierNegotiationQuoteItems = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Tedarikçi müzakereli teklif öğeleri yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.supplierNegotiationQuoteItems = errorMessage;
    });

    // Get PORequest User List
    builder.addCase(getPORequestUserList.pending, (state) => {
      state.operationLoading.poRequestUserList = true;
      state.error = null;
      state.errors.poRequestUserList = null;
    });
    builder.addCase(getPORequestUserList.fulfilled, (state, action) => {
      state.operationLoading.poRequestUserList = false;
      const responseData = action.payload;
      if (Array.isArray(responseData)) {
        state.poRequestUserList = responseData;
      } else if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const data = (responseData as any).data;
        if (Array.isArray(data)) {
          state.poRequestUserList = data;
        }
      }
      state.error = null;
      state.errors.poRequestUserList = null;
    });
    builder.addCase(getPORequestUserList.rejected, (state, action) => {
      state.operationLoading.poRequestUserList = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Kullanıcı listesi yüklenirken hata oluştu';
      state.error = errorMessage;
      state.errors.poRequestUserList = errorMessage;
    });

    // Set PORequest Pending by User
    builder.addCase(setPORequestPendingByUser.pending, (state) => {
      state.operationLoading.setPendingByUser = true;
      state.error = null;
      state.errors.setPendingByUser = null;
    });
    builder.addCase(setPORequestPendingByUser.fulfilled, (state, action) => {
      state.operationLoading.setPendingByUser = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { createdBy } = action.payload;
      // Bu endpoint kullanıcının tüm taleplerini beklemede durumuna alır
      // State'de bu kullanıcının taleplerini güncelle
      state.items = state.items.map(item => 
        item.createdBy === createdBy 
          ? { ...item, requestStatus: 'Pending' }
          : item
      );
      state.error = null;
      state.errors.setPendingByUser = null;
    });
    builder.addCase(setPORequestPendingByUser.rejected, (state, action) => {
      state.operationLoading.setPendingByUser = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Kullanıcı taleplerini beklemede durumuna alma başarısız';
      state.error = errorMessage;
      state.errors.setPendingByUser = errorMessage;
    });

    // Approve PORequest (Bulk approval)
    builder.addCase(approvePORequest.pending, (state) => {
      state.operationLoading.approve = true;
      state.error = null;
      state.errors.approve = null;
    });
    builder.addCase(approvePORequest.fulfilled, (state, action) => {
      state.operationLoading.approve = false;
      // Veriyi göster ama cache'leme yapma - her seferinde fresh data
      const { id, approvedQuantity } = action.payload;
      state.items = state.items.map(item => 
        item.id === id 
          ? { ...item, requestStatus: 'Approved', approvedQuantity: approvedQuantity }
          : item
      );
      state.error = null;
      state.errors.approve = null;
    });
    builder.addCase(approvePORequest.rejected, (state, action) => {
      state.operationLoading.approve = false;
      const errorMessage = typeof action.payload === 'string' ? action.payload : 'Toplu onaylama işlemi başarısız';
      state.error = errorMessage;
      state.errors.approve = errorMessage;
    });

    // Get PORequest Status Counts
    builder.addCase(getStatusCounts.pending, (state) => {
      state.operationLoading.statusCounts = true;
      state.errors.statusCounts = null;
    });
    builder.addCase(getStatusCounts.fulfilled, (state, action) => {
      state.operationLoading.statusCounts = false;
      state.statusCounts = action.payload;
      state.errors.statusCounts = null;
    });
    builder.addCase(getStatusCounts.rejected, (state, action) => {
      state.operationLoading.statusCounts = false;
      state.errors.statusCounts = typeof action.payload === 'string' ? action.payload : 'Durum sayıları yüklenirken hata oluştu';
    });

    // Get Status Matrix
    builder.addCase(getStatusMatrix.pending, (state) => {
      state.operationLoading.statusMatrix = true;
      state.errors.statusMatrix = null;
    });
    builder.addCase(getStatusMatrix.fulfilled, (state, action) => {
      state.operationLoading.statusMatrix = false;
      state.statusMatrix = action.payload;
      state.errors.statusMatrix = null;
    });
    builder.addCase(getStatusMatrix.rejected, (state, action) => {
      state.operationLoading.statusMatrix = false;
      state.errors.statusMatrix = typeof action.payload === 'string' ? action.payload : 'Status matrix yüklenirken hata oluştu';
    });
  },
});

export const { 
  clearError, 
  clearSpecificError,
  clearPORequests, 
  setSelectedItems, 
  toggleSelectedItem, 
  clearSelectedItems,
  setFilters,
  clearFilters,
  clearQuoteItems,
  clearEvaluationReadyItems,
  clearAwardedSuppliers,
  clearNegotiationSuppliers,
  clearSupplierAwardedQuoteItems,
  clearSupplierNegotiationQuoteItems,
  clearPORequestUserList,
  clearStatusCounts,
  clearStatusMatrix
} = poRequestSlice.actions;
export default poRequestSlice.reducer;

